import { createAcMatcher } from './AcAutomaton';
export function ensureAsyncIterable(stream) {
    if (stream[Symbol.asyncIterator]) {
        return stream;
    }
    const iterator = async function* () {
        const reader = stream.getReader();
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    return;
                }
                yield value;
            }
        }
        finally {
            reader.releaseLock();
        }
    };
    Object.assign(stream, {
        [Symbol.asyncIterator]: iterator,
    });
    return stream;
}
const createMatcher = createAcMatcher;
export class StreamParser {
    parsers;
    defaultHandler;
    static create(parsers, defaultHandler) {
        const { readable, writable } = new TransformStream();
        const writer = writable.getWriter();
        const instance = new StreamParser(parsers, defaultHandler);
        const promise = instance.parse(readable);
        return {
            close: async () => {
                await writer.close();
                await promise;
            },
            write: async (data) => {
                await writer.write(data);
            },
        };
    }
    constructor(parsers, defaultHandler) {
        this.parsers = parsers;
        this.defaultHandler = defaultHandler;
    }
    createHandlerInstance(handler, startMatch) {
        const { readable, writable } = new TransformStream();
        const writer = writable.getWriter();
        const handlerPromise = handler(ensureAsyncIterable(readable), startMatch);
        const close = async () => {
            await writer.close();
            await handlerPromise;
        };
        return {
            writer,
            close,
        };
    }
    async parse(stream) {
        const { writer: defaultWriter, close: closeDefaultHandler } = this.createHandlerInstance(this.defaultHandler, null);
        const startMatcher = createMatcher(this.parsers.map(p => [p.start, p]));
        let matchHandlerInstance = null;
        let endMatcher = null;
        const openMatchHandler = (result) => {
            matchHandlerInstance = this.createHandlerInstance(result.value.handler, result.match);
            endMatcher = createMatcher([[result.value.end, result.value]]);
            if (result.postfix) {
                matchHandlerInstance.writer.write(result.postfix);
            }
        };
        const closeMatchHandler = async () => {
            if (!matchHandlerInstance)
                return;
            await matchHandlerInstance.close();
            matchHandlerInstance = null;
            endMatcher = null;
        };
        const handleChar = async (char) => {
            const matcher = matchHandlerInstance
                ? endMatcher
                : startMatcher;
            const writer = matchHandlerInstance
                ? matchHandlerInstance.writer
                : defaultWriter;
            const result = matcher(char);
            if (result === null)
                return;
            if (typeof result === 'string') {
                writer.write(result);
                return;
            }
            if (result.type === 'match') {
                if (!matchHandlerInstance) {
                    openMatchHandler(result);
                }
                else {
                    await closeMatchHandler();
                }
            }
        };
        for await (const chunk of ensureAsyncIterable(stream)) {
            for await (const char of chunk) {
                await handleChar(char);
            }
        }
        await handleChar(null);
        if (matchHandlerInstance) {
            await closeMatchHandler();
        }
        await closeDefaultHandler();
    }
}
export default StreamParser;
